import { Box, Skeleton, Stack } from "@mui/material";
import { Button } from "src/components-mui/base/button/button";

const DOTS_INDICATOR = -9999;
const TABLE_PAGINATION_NUMBER_BUTTON_TEST_ID = "table-pagination-number-button";

const NumbersLoadingSkeleton = () => (
    <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
    </Stack>
);

interface ITablePaginationMenu {
    showSkeleton: boolean;
    isDisabled: boolean;
    currentPageIndex: number;
    pageIndexes: number[];
    isMobile?: boolean;
    onPageSelect: (index: number) => void;
}

export const TablePaginationNumbers = ({ pageIndexes, currentPageIndex, showSkeleton, isDisabled, isMobile, onPageSelect }: ITablePaginationMenu) => {
    const getMappedPaginationOptions = () => {
        if (pageIndexes.length <= 4) {
            return pageIndexes;
        }

        const lastIndex = pageIndexes.length - 1;
        let indexes: number[];

        if (currentPageIndex === pageIndexes[lastIndex]) {
            indexes = isMobile ? pageIndexes.slice(-2) : pageIndexes.slice(-3);
        } else {
            indexes = isMobile ? pageIndexes.slice(0, 2) : pageIndexes.slice(0, 3);
        }

        if (currentPageIndex > 0 && currentPageIndex < lastIndex) {
            indexes = isMobile ? [currentPageIndex] : [currentPageIndex - 1, currentPageIndex, currentPageIndex + 1];
        }

        const shouldShowLeftDots = currentPageIndex > (isMobile ? 1 : 2);
        const shouldShowRightDots = currentPageIndex < pageIndexes.length - (isMobile ? 2 : 3);
        const shouldIncludeFirstItem = currentPageIndex > (isMobile ? 0 : 1);
        const shouldIncludeLastItem = currentPageIndex < (isMobile ? lastIndex : pageIndexes.length - 2);

        if (shouldShowRightDots) {
            indexes.push(DOTS_INDICATOR);
        }

        if (shouldShowLeftDots) {
            indexes = [DOTS_INDICATOR, ...indexes];
        }

        if (shouldIncludeFirstItem) {
            indexes = [pageIndexes[0], ...indexes];
        }

        if (shouldIncludeLastItem) {
            indexes.push(pageIndexes[lastIndex]);
        }

        return indexes;
    };

    return (
        <Stack
            direction="row"
            spacing={1}
            sx={{
                alignItems: "center",
            }}
        >
            {showSkeleton ? (
                <NumbersLoadingSkeleton />
            ) : (
                getMappedPaginationOptions().map((index, i) =>
                    index === DOTS_INDICATOR ? (
                        <Box key={index + i}>...</Box>
                    ) : (
                        <Button
                            size="extraSmall"
                            onClick={() => onPageSelect(index)}
                            key={index + i}
                            disabled={isDisabled && index !== currentPageIndex}
                            variant={index === currentPageIndex ? "contained" : "text"}
                            aria-current={index === currentPageIndex ? "page" : undefined}
                            data-test-id={TABLE_PAGINATION_NUMBER_BUTTON_TEST_ID}
                            sx={{
                                padding: 0,
                                "&.MuiButton-text:hover": {
                                    backgroundColor: "gray.100",
                                    color: "gray.800",
                                },
                            }}
                        >
                            {index + 1}
                        </Button>
                    )
                )
            )}
        </Stack>
    );
};
